import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    History,
    Search,
    Filter,
    ArrowDownRight,
    ArrowUpRight,
    Package,
    Calendar,
    Download,
    Trash2
} from 'lucide-react';
import toast from 'react-hot-toast';
import { getAllInventoryLogs, deleteInventoryLogAndRevertStock } from '../../../services/inventoryLogService';
import { fetchProducts } from '../../../redux/slices/productSlice';
import * as XLSX from 'xlsx';

const ProductHistory = () => {
    const dispatch = useDispatch();
    const { products } = useSelector((state) => state.product);

    const [logs, setLogs] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isDeleting, setIsDeleting] = useState(false);

    // Filters
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedProduct, setSelectedProduct] = useState('All');
    const [selectedType, setSelectedType] = useState('All');
    const [dateRange, setDateRange] = useState({
        from: '',
        to: new Date().toISOString().split('T')[0]
    });

    useEffect(() => {
        dispatch(fetchProducts());
        loadLogs();
    }, [dispatch]);

    const loadLogs = async () => {
        try {
            setIsLoading(true);
            const data = await getAllInventoryLogs();
            setLogs(data);
        } catch (error) {
            toast.error("Failed to load inventory logs");
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeleteLog = async (log) => {
        if (window.confirm(`Are you sure you want to delete this log for ${log.productName} and REVERT the stock change (${log.quantityChange > 0 ? '+' : ''}${log.quantityChange})?`)) {
            try {
                setIsDeleting(true);
                await deleteInventoryLogAndRevertStock(log.id);
                toast.success('Log deleted and stock reverted successfully');
                loadLogs();
                dispatch(fetchProducts());
            } catch (error) {
                console.error(error);
                toast.error('Failed to delete log and revert stock');
            } finally {
                setIsDeleting(false);
            }
        }
    };

    // Extract unique transaction types for filter
    const transactionTypes = useMemo(() => {
        const types = new Set(logs.map(log => log.transactionType));
        return ['All', ...Array.from(types).filter(Boolean)];
    }, [logs]);

    // Apply filters
    const filteredLogs = useMemo(() => {
        return logs.filter(log => {
            // Search filter
            const matchesSearch =
                (log.productName?.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (log.referenceId?.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (log.customerName?.toLowerCase().includes(searchTerm.toLowerCase()));

            // Product filter
            const matchesProduct = selectedProduct === 'All' || log.productId === selectedProduct;

            // Type filter
            const matchesType = selectedType === 'All' || log.transactionType === selectedType;

            // Date filter
            const logDate = log.date;
            const matchesDateFrom = !dateRange.from || logDate >= dateRange.from;
            const matchesDateTo = !dateRange.to || logDate <= dateRange.to;

            return matchesSearch && matchesProduct && matchesType && matchesDateFrom && matchesDateTo;
        });
    }, [logs, searchTerm, selectedProduct, selectedType, dateRange]);

    const exportToExcel = () => {
        if (filteredLogs.length === 0) {
            toast.error("No data to export");
            return;
        }
        const exportData = filteredLogs.map(log => ({
            'Date': log.date,
            'Time': new Date(log.createdAt).toLocaleTimeString(),
            'Product': log.productName,
            'Transaction Type': log.transactionType,
            'Reference ID': log.referenceId || '--',
            'Customer / Supplier': log.customerName || '--',
            'Previous Stock': log.previousStock,
            'Change': log.quantityChange > 0 ? `+${log.quantityChange}` : log.quantityChange,
            'New Stock': log.newStock
        }));

        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Inventory_History");
        XLSX.writeFile(wb, `Product_Usage_History_${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    return (
        <div className="bg-slate-50 min-h-screen p-4 md:p-6 lg:p-8">
            {/* Header */}
            <div className="mb-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                        <History className="w-7 h-7 text-indigo-600" />
                        Product Usage History
                    </h1>
                    <p className="text-sm text-slate-500 mt-1">
                        Track all stock additions, deductions, and sales across the system.
                    </p>
                </div>
                <button
                    onClick={exportToExcel}
                    className="flex items-center gap-2 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 px-4 py-2 rounded-xl font-medium transition-colors border border-indigo-200"
                >
                    <Download className="w-4 h-4" /> Export Excel
                </button>
            </div>

            {/* Filters */}
            <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-200 mb-6 space-y-4">
                <div className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-2">
                    <Filter className="w-4 h-4" /> Filters
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                    {/* Search */}
                    <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Search className="w-4 h-4 text-slate-400" />
                        </div>
                        <input
                            type="text"
                            placeholder="Search PRD, Ref ID, Name..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                        />
                    </div>

                    {/* Product Filter */}
                    <div>
                        <select
                            value={selectedProduct}
                            onChange={(e) => setSelectedProduct(e.target.value)}
                            className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                        >
                            <option value="All">All Products</option>
                            {products.map(p => (
                                <option key={p.id} value={p.id}>{p.name || p.productName}</option>
                            ))}
                        </select>
                    </div>

                    {/* Type Filter */}
                    <div>
                        <select
                            value={selectedType}
                            onChange={(e) => setSelectedType(e.target.value)}
                            className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                        >
                            {transactionTypes.map(type => (
                                <option key={type} value={type}>{type}</option>
                            ))}
                        </select>
                    </div>

                    {/* Dates */}
                    <div>
                        <input
                            type="date"
                            value={dateRange.from}
                            onChange={(e) => setDateRange({ ...dateRange, from: e.target.value })}
                            className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 outline-none text-slate-600"
                            placeholder="From Date"
                        />
                    </div>
                    <div>
                        <input
                            type="date"
                            value={dateRange.to}
                            onChange={(e) => setDateRange({ ...dateRange, to: e.target.value })}
                            className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 outline-none text-slate-600"
                            placeholder="To Date"
                        />
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500 font-bold">
                                <th className="px-6 py-4">Date & Time</th>
                                <th className="px-6 py-4">Product</th>
                                <th className="px-6 py-4">Transaction Details</th>
                                <th className="px-6 py-4 text-center">Stock Change</th>
                                <th className="px-6 py-4 text-center">Final Stock</th>
                                <th className="px-6 py-4 text-center">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 text-sm">
                            {isLoading && (
                                <tr>
                                    <td colSpan="6" className="px-6 py-12 text-center text-slate-400">
                                        <div className="flex flex-col items-center gap-3">
                                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                                            Loading history...
                                        </div>
                                    </td>
                                </tr>
                            )}

                            {!isLoading && filteredLogs.length === 0 && (
                                <tr>
                                    <td colSpan="6" className="px-6 py-12 text-center text-slate-400">
                                        <div className="flex flex-col items-center gap-3">
                                            <Package className="w-10 h-10 text-slate-200" />
                                            No logs found matching your filters.
                                        </div>
                                    </td>
                                </tr>
                            )}

                            {!isLoading && filteredLogs.length > 0 &&
                                filteredLogs.map(log => (
                                    <tr key={log.id} className="hover:bg-slate-50 transition-colors">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="font-semibold text-slate-900">
                                                {new Date(log.date).toLocaleDateString('en-IN', {
                                                    day: 'numeric', month: 'short', year: 'numeric'
                                                })}
                                            </div>
                                            <div className="text-xs text-slate-400 mt-0.5">
                                                {new Date(log.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="font-semibold text-slate-900">{log.productName}</div>
                                            <div className="text-[11px] text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full inline-block mt-1">
                                                ID: {log.productId?.substring(0, 8)}...
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-2">
                                                <span className="px-2 py-1 bg-slate-100 text-slate-700 text-xs font-semibold rounded-lg">
                                                    {log.transactionType}
                                                </span>
                                            </div>
                                            <div className="text-xs text-slate-500 mt-1.5 flex items-center gap-2">
                                                <span className="font-medium">Ref:</span> {log.referenceId || '--'} |
                                                <span className="font-medium ml-1">By/To:</span> <span className="text-indigo-600 font-medium">{log.customerName || '--'}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            <div className={`inline-flex items-center gap-1 font-bold px-3 py-1 rounded-xl ${log.quantityChange > 0
                                                ? 'bg-emerald-50 text-emerald-600 border border-emerald-100'
                                                : log.quantityChange < 0
                                                    ? 'bg-rose-50 text-rose-600 border border-rose-100'
                                                    : 'bg-slate-100 text-slate-600'
                                                }`}>
                                                {log.quantityChange > 0 ? <ArrowUpRight className="w-3.5 h-3.5" /> : log.quantityChange < 0 ? <ArrowDownRight className="w-3.5 h-3.5" /> : null}
                                                {log.quantityChange > 0 ? '+' : ''}{log.quantityChange}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            <div className="font-bold text-slate-900 text-base">{log.newStock}</div>
                                            <div className="text-[10px] text-slate-400">was {log.previousStock}</div>
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            <button
                                              onClick={() => handleDeleteLog(log)}
                                              disabled={isDeleting}
                                              className="p-2 text-rose-500 hover:bg-rose-50 rounded-lg transition-colors disabled:opacity-50"
                                              title="Delete Log & Revert Stock"
                                            >
                                              <Trash2 className="w-4 h-4" />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            }
                        </tbody>
                    </table>
                </div>
                <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 flex items-center justify-between">
                    <span className="text-xs font-semibold text-slate-500">
                        Showing {filteredLogs.length} entries
                    </span>
                </div>
            </div>
        </div>
    );
};

export default ProductHistory;
